import { Fragment, useState } from 'react';
import InvestmentGraph from "./InvestmentGraph";
import type { ChartDataResponse } from "../../Types/Entities";


const suffixMap: { [key: string]: string } = {
    'Løn': ' kr.', 'Bidrag': ' %', 'Depot': ' kr.',
    'Afkast': ' %', 'ÅOP': ' %', 'AUM Rabat': ' %', 'AMB': ' %', 'Inflation': ' %', 'PAL': ' %',
    'Depotvederlag': ' kr.', 'DepotMaksVederlag': ' kr.', 'Depotomkostninger': ' kr.', 'DepotMaksOmk': ' kr.',
    'Ind. Omk.': ' kr.', 'IndMaksOmk': ' kr.', 'Ind. Vederlag': ' kr.', 'IndMaksVederlag': ' kr.',
    'Gebyr': ' kr.', 'Fast Vederlag': ' kr.', 'Tilskud': ' kr.', 'TilskudÅr': ' år', 'Etablering': ' kr.'
};
const percentageFields = new Set(
    Object.entries(suffixMap).filter(([_, s]) => s === ' %').map(([k]) => k)
);
const fields = [
    { title: 'Kundeoplysninger', keys: ['Løn','Bidrag','Depot'] },
    { title: 'Afkast og investeringsomkostninger', keys: ['Afkast','ÅOP','AUM Rabat','AMB','Inflation','PAL'] },
    { title: 'Øvrige omkostninger', keys: ['Depotvederlag','DepotMaksVederlag','Depotomkostninger','DepotMaksOmk','Ind. Omk.','IndMaksOmk','Ind. Vederlag','IndMaksVederlag','Gebyr','Fast Vederlag','Tilskud','TilskudÅr','Etablering'] }
];
const forsikringFields = ['TAE','RFY','Død','KS','Præmiefritagelse','Sundhed'];
const riskProfiles = {
    Lav: { Afkast:3, ÅOP:0.5, 'AUM Rabat':0.1, AMB:0.05, Inflation:2, PAL:0.1, Depotvederlag:0.3, Depotomkostninger:0.2, 'Ind. Omk.':0.1, 'Ind. Vederlag':0.1, Gebyr:100, 'Fast Vederlag':200, DepotMaksOmk:100000, DepotMaksVederlag:100000, IndMaksOmk:100000, IndMaksVederlag:100000 },
    Mellem: { Afkast:5, ÅOP:0.7, 'AUM Rabat':0.15, AMB:0.1, Inflation:2, PAL:0.1, Depotvederlag:0.5, Depotomkostninger:0.3, 'Ind. Omk.':0.2, 'Ind. Vederlag':0.15, Gebyr:150, 'Fast Vederlag':300, DepotMaksOmk:150000, DepotMaksVederlag:150000, IndMaksOmk:150000, IndMaksVederlag:150000 },
    Høj: { Afkast:7, ÅOP:1.0, 'AUM Rabat':0.2, AMB:0.15, Inflation:2, PAL:0.1, Depotvederlag:0.7, Depotomkostninger:0.5, 'Ind. Omk.':0.3, 'Ind. Vederlag':0.25, Gebyr:200, 'Fast Vederlag':400, DepotMaksOmk:200000, DepotMaksVederlag:200000, IndMaksOmk:200000, IndMaksVederlag:200000 }
};

export default function ComparisonForm() {
    const [formA, setFormA] = useState<{[k:string]:string}>({});
    const [comparisons, setComparisons] = useState<{[k:string]:string}[]>([{}]);
    const [chartData, setChartData] = useState<ChartDataResponse|null>(null);
    const [startYear, setStartYear] = useState('');
    const [startMonth, setStartMonth] = useState('');
    const [endYear, setEndYear] = useState('');
    const [endMonth, setEndMonth] = useState('');
    const [lockFields, setLockFields] = useState(true);
    const [riskTier, setRiskTier] = useState<'Lav'|'Mellem'|'Høj'>('Lav');

    const parseForm = (f:{[k:string]:string}) => {
        const out:{[k:string]:number} = {
            StartÅr: parseInt(startYear,10), StartMåned: parseInt(startMonth,10),
            SlutÅr: parseInt(endYear,10), SlutMåned: parseInt(endMonth,10)
        };
        Object.entries(f).forEach(([k,v]) => { if(!v) return; const n=parseFloat(v); out[k]=percentageFields.has(k)?n/100:n; });
        return out;
    };

    const handleSubmit = async () => {
        try {
            const payload = { base: parseForm(formA), comparisons: comparisons.map(parseForm) };
            const res = await fetch('api/Calculation',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
            if(!res.ok) throw new Error();
            setChartData(await res.json());
        } catch { alert('Submission failed'); }
    };

    const handleChangeA = (key:string,value:string) => {
        setFormA(prev=>({ ...prev,[key]:value }));
        if(!key.includes('_Pris')) setComparisons(prev=>prev.map(c=>({ ...c,[key]:value })));
    };
    const handleChangeComp = (idx:number,key:string,value:string) => {
        setComparisons(prev=>{ const u=[...prev]; u[idx]={ ...u[idx],[key]:value }; return u; });
    };
    const applyRiskProfileA = () => { const vals=riskProfiles[riskTier];
        setFormA(prev=>({ ...prev,...Object.fromEntries(Object.entries(vals).map(([k,v])=>[k,v.toString()])) }));
        setComparisons(prev=>prev.map(c=>({ ...c,...Object.fromEntries(Object.entries(vals).map(([k,v])=>[k,v.toString()])) })));
    };
    const applyRiskProfileComp = (idx:number) => { const vals=riskProfiles[riskTier];
        setComparisons(prev=>{ const u=[...prev]; u[idx]={ ...u[idx],...Object.fromEntries(Object.entries(vals).map(([k,v])=>[k,v.toString()])) }; return u; });
    };

    const getSuffix=(k:string)=>suffixMap[k]||'';
    const renderField=(form:'A'|number,k:string,label:string)=>(
        <div key={k} className="relative">
            <input type="number" placeholder={label} value={(form==='A'?formA:comparisons[form])[k]||''}
                   disabled={lockFields && form!=='A'? false : lockFields && !fields[0].keys.includes(k) && !forsikringFields.some(f=>k.startsWith(f))}
                   className="input input-bordered w-full pr-12 disabled:bg-gray-100 disabled:cursor-not-allowed"
                   onChange={e=> form==='A'?handleChangeA(k,e.target.value):handleChangeComp(form,k,e.target.value)} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">{getSuffix(k)}</span>
        </div>
    );
    const renderForsikring=(form:'A'|number)=>(
        <div className="grid grid-cols-3 gap-4 items-center">
            {forsikringFields.map(label=>(
                <Fragment key={label}>
                    <div className="font-medium flex items-center">{label}</div>
                    {renderField(form,`${label}_Dækning`,'Dækning')}
                    {renderField(form,`${label}_Pris`,'Pris')}
                </Fragment>
            ))}
        </div>
    );
    const renderDateSelectors=()=>(
        <div className="mb-4 grid grid-cols-2 gap-4">
            <div className="grid grid-cols-2 gap-2">
                <select className="select select-bordered" value={startMonth} onChange={e=>setStartMonth(e.target.value)}>
                    <option value="">Måned start</option>{[...Array(12)].map((_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                </select>
                <input type="number" className="input input-bordered" placeholder="År start" value={startYear} onChange={e=>setStartYear(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
                <select className="select select-bordered" value={endMonth} onChange={e=>setEndMonth(e.target.value)}>
                    <option value="">Måned slut</option>{[...Array(12)].map((_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                </select>
                <input type="number" className="input input-bordered" placeholder="År slut" value={endYear} onChange={e=>setEndYear(e.target.value)} />
            </div>
        </div>
    );

    return (
        <div className="flex justify-center p-10 w-screen">
            <div className="w-full max-w-3xl">

                {/* Top controls */}
                <div className="flex gap-4 mb-6">
                    <label className="flex items-center gap-2">
                        <input type="checkbox" className="toggle" checked={lockFields}
                               onChange={() => setLockFields(prev => !prev)}/>
                        <span>Lås ikke-kunde felter</span>
                    </label>
                    <select className="select select-bordered" value={riskTier}
                            onChange={e => setRiskTier(e.target.value as any)}>
                        {Object.keys(riskProfiles).map(r => <option key={r}>{r}</option>)}
                    </select>
                </div>

                {/* Form A */}
                <div className="mb-6 card bg-base-100 shadow-lg p-6">
                    <h2 className="text-2xl font-bold mb-4">Afkastberegner A</h2>
                    {renderDateSelectors()}
                    {fields.map(section => (
                        <div key={section.title} className="collapse collapse-arrow mb-4">
                            <input type="checkbox"/>
                            <div className="collapse-title font-medium">{section.title}</div>
                            <div className="collapse-content grid grid-cols-2 gap-4">
                                {section.keys.map(k => renderField('A', k, k === 'TilskudÅr' ? 'Antal år' : k))}
                            </div>
                        </div>
                    ))}
                    <div className="collapse collapse-arrow mb-4">
                        <input type="checkbox"/>
                        <div className="collapse-title font-medium">Forsikring</div>
                        <div className="collapse-content">{renderForsikring('A')}</div>
                    </div>
                    <div className="flex gap-2">
                        <button className="btn btn-sm" onClick={applyRiskProfileA}>Anvend risikoprofil</button>
                    </div>
                </div>

                {/* Comparison forms */}
                {comparisons.map((_, idx) => (
                    <div key={idx} className="mb-6 card bg-base-100 shadow-lg p-6">
                        <h2 className="text-2xl font-bold mb-4">Afkastberegner {String.fromCharCode(66 + idx)}</h2>
                        {renderDateSelectors()}
                        {fields.map(section => (
                            <div key={section.title} className="collapse collapse-arrow mb-4">
                                <input type="checkbox"/>
                                <div className="collapse-title font-medium">{section.title}</div>
                                <div className="collapse-content grid grid-cols-2 gap-4">
                                    {section.keys.map(k => renderField(idx, k, k === 'TilskudÅr' ? 'Antal år' : k))}
                                </div>
                            </div>
                        ))}
                        <div className="collapse collapse-arrow mb-4">
                            <input type="checkbox"/>
                            <div className="collapse-title font-medium">Forsikring</div>
                            <div className="collapse-content">{renderForsikring(idx)}</div>
                        </div>
                        <div className="flex gap-2">
                            <button className="btn btn-sm" onClick={() => applyRiskProfileComp(idx)}>Anvend
                                risikoprofil
                            </button>
                        </div>
                    </div>
                ))}

                <div className="flex gap-[30em]">
                    <button className="btn btn-primary"
                            onClick={() => setComparisons(prev => [...prev, {}])}>Tilføj
                        sammenligning
                    </button>

                    <button className="btn btn-secondary" onClick={handleSubmit}>Beregn</button>
                </div>

                {/* Graph */}
                <div className="mt-10">
                    {chartData && <InvestmentGraph data={chartData}/>}
                </div>

            </div>
        </div>
    );
}